import pencilIcon from "@assets/images/pencil-alt.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import { SubmitFilterDataType } from "@saleor/changeOrderExport/components/OrderFilterSection/constant";
import {
  changeOrderPath,
  ExporOrderListSortField
} from "@saleor/changeOrderExport/urls";
import { ViewEditMode } from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import DialogCreateOrderError from "@saleor/createOrder/components/DialogCreateOrderError";
import {
  SapChangeOrderQuery,
  useGetCreditLimitLazyQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
// import moment from "moment-timezone";
import React, { useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

enum EStatusType {
  "Received Order" = "#A3AED0",
  "Credit/Cash Issue" = "#FFC107",
  "Partial Committed Order" = "#3860BE",
  "Ready for delivery" = "#007BFF",
  "Partial Delivery" = "#009FB8",
  "Completed Delivery" = "#6CC04A",
  "Cancelled" = "#E41B17",
  "Completed Order" = "#1E7D34"
}

export interface IOrderListTable
  extends ListProps,
    SortPage<ExporOrderListSortField> {
  data: SapChangeOrderQuery["sapChangeOrder"];
  settings: any;
  onUpdateListSettings: any;
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  filter?: SubmitFilterDataType;
}

const STATUSES = {
  COMPLETED_ORDER: "Completed Order",
  COMPLETE_ORDER: "Complete Order",
  COMPLETE_DELIVERY: "Completed Delivery"
};

const useStyles = makeStyles(
  theme => ({
    root: {
      overflowX: "auto",
      width: "100%",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderBottom: 0,
      borderRadius: "2px",
      "& .MuiTableCell-head": {
        "& *": {
          color: theme.palette.grey[600],
          whiteSpace: "nowrap"
        }
      },
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      }
    },
    tableContainer: {
      maxHeight: "60vh"
    },
    cursorPointer: {
      cursor: "pointer"
    },
    link: {
      cursor: "pointer"
    },
    colPiNumber: {
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      },
      left: 0,
      background: "inherit",
      borderRight: `2px solid ${theme.palette.grey[300]}`,
      position: "sticky",
      zIndex: 3
    },
    col120: {
      minWidth: 120,
      color: theme.palette.grey[900]
    },
    col150: {
      minWidth: 150,
      color: theme.palette.grey[900]
    },
    col180: {
      minWidth: 180,
      color: theme.palette.grey[900]
    },
    pagination: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    paginateFooter: {
      background: "rgba(212, 244, 248, 0.8)",
      padding: "12px 20px",
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      borderLeft: `1px solid ${theme.palette.grey[300]}`
    },
    rowClickable: {
      cursor: "pointer"
    },
    colFreeze: {
      position: "sticky",
      right: 0,
      borderLeft: `1px solid ${theme.palette.grey[300]}`,
      minWidth: 48,
      padding: 0,
      boxShadow: "-4px 0px 8px rgb(0 0 0 / 4%)",
      borderRadius: "0px 2px 2px 0px",
      background: "inherit",
      textAlign: "center"
    },
    disabledBtn: {
      pointerEvents: "none",
      opacity: 0.3,
      cursor: "default"
    },
    statusIcon: {
      width: 8,
      height: 8,
      marginRight: 8,
      borderRadius: "50%"
    },
    title: {
      marginTop: 40,
      marginBottom: 24
    },
    flex: { display: "flex", alignItems: "center" },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    },
    zIndex1: {
      zIndex: 1
    }
  }),
  { name: "OrderListTable" }
);

const OrderListTable: React.FC<IOrderListTable> = props => {
  const {
    data,
    disabled,
    settings,
    totalCount,
    pageInfo,
    latestPageItemNumber,
    onUpdateListSettings,
    sort,
    onSort,
    onPreviousPage,
    onNextPage,
    onNextLastPage,
    onPreviousFirstPage,
    filter
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigator();
  const [_, dispatchAppState] = useAppState();
  const [currentSoNo, setCurrentSoNo] = useState<string>("");
  const [openCreateOrderError, setOpenCreateOrderError] = useState<boolean>(
    false
  );

  const isStaff = isStaffRole();

  const toggleCloseCreateOrderError = () => {
    setOpenCreateOrderError(false);
  };

  const renderStatus = (item: string) => (
    <div className={classes.flex}>
      <div
        className={classes.statusIcon}
        style={{ backgroundColor: EStatusType[item] }}
      ></div>
      <MyTypography variant="body3">{item}</MyTypography>
    </div>
  );

  const viewOrder = (_soNo: string) => {
    dispatchAppState({
      type: "setViewEditMode",
      payload: {
        viewEditMode: ViewEditMode.VIEW
      }
    });
    dispatchAppState({
      type: "setFilterOrder",
      payload: {
        filterOrder: filter
      }
    });
    setCurrentSoNo(_soNo);
    navigate(changeOrderPath(_soNo));
  };

  const [callCreditLimit] = useGetCreditLimitLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: dataCreditLimit => {
      if (currentSoNo) {
        if (isStaff) {
          navigate(changeOrderPath(currentSoNo));
        } else {
          if (dataCreditLimit?.getCreditLimit) {
            if (dataCreditLimit?.getCreditLimit?.creditBlockStatus) {
              setOpenCreateOrderError(true);
              return;
            }
            dispatchAppState({
              type: "setViewEditMode",
              payload: {
                viewEditMode: ViewEditMode.EDIT
              }
            });
            dispatchAppState({
              type: "setFilterOrder",
              payload: {
                filterOrder: filter
              }
            });
            navigate(changeOrderPath(currentSoNo));
          }
        }
      }
    }
  });

  const isButtonDisabled = item =>
    [
      STATUSES.COMPLETED_ORDER,
      STATUSES.COMPLETE_DELIVERY,
      STATUSES.COMPLETE_ORDER
    ].includes(item.orderStatusEOrdering);

  const editOrder = (soNo, contractNo, salesOrgCode, soldToCode) => {
    dispatchAppState({
      type: "setViewEditMode",
      payload: {
        viewEditMode: ViewEditMode.EDIT
      }
    });
    setCurrentSoNo(soNo);
    dispatchAppState({
      type: "setFilterOrder",
      payload: {
        filterOrder: filter
      }
    });
    callCreditLimit({
      variables: {
        input: {
          contractNo,
          salesOrgCode,
          soldToCode
        }
      }
    });
  };

  return (
    <>
      <MyTypography className={classes.title} variant="h7">
        {intl.formatMessage({ defaultMessage: "รายการที่เกี่ยวข้อง" })}
      </MyTypography>

      <div className={classes.root} ref={containerRef}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader data-testid="orderList-table">
            <TableHead>
              <TableRow>
                <TableCellHeader
                  className={classes.colPiNumber}
                  direction={
                    sort.sort === ExporOrderListSortField.piNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(ExporOrderListSortField.piNo)}
                >
                  {intl.formatMessage({ defaultMessage: "PI No." })}
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  style={{ paddingLeft: "3.2rem" }}
                  direction={
                    sort.sort === ExporOrderListSortField.eoNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(ExporOrderListSortField.eoNo)}
                >
                  {intl.formatMessage({ defaultMessage: "EO No." })}
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === ExporOrderListSortField.poNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(ExporOrderListSortField.poNo)}
                >
                  {intl.formatMessage({ defaultMessage: "PO No." })}
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === ExporOrderListSortField.soldTo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(ExporOrderListSortField.soldTo)}
                >
                  {intl.formatMessage({ defaultMessage: "Sold to" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col180}>
                  {intl.formatMessage({ defaultMessage: "Ship to" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col150}>
                  {intl.formatMessage({ defaultMessage: "Country" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  {intl.formatMessage({ defaultMessage: "Incoterm" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  {intl.formatMessage({ defaultMessage: "Payment" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  {intl.formatMessage({ defaultMessage: "BU" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  {intl.formatMessage({ defaultMessage: "Company" })}
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === ExporOrderListSortField.createDate
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(ExporOrderListSortField.createDate)}
                >
                  {intl.formatMessage({ defaultMessage: "Create Date" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col180}>
                  {intl.formatMessage({ defaultMessage: "Order Status (SAP)" })}
                </TableCellHeader>
                <TableCellHeader className={classes.col180}>
                  {intl.formatMessage({ defaultMessage: "Status" })}
                </TableCellHeader>
                <TableCellHeader
                  className={classes.colFreeze}
                ></TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderCollection(data, (item, index) => (
                <TableRow className={classes.rowClickable} key={index}>
                  <TableCell
                    className={classNames(classes.colPiNumber, classes.zIndex1)}
                  >
                    <MyTypography
                      variant="subtitle3"
                      onClick={() => viewOrder(item.soNo)}
                    >
                      {item ? item.contractNo : <Skeleton />}
                    </MyTypography>
                  </TableCell>
                  <TableCell
                    className={classes.col120}
                    style={{ paddingLeft: "3.2rem" }}
                  >
                    {item ? item.soNo : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.poNo : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col180}>
                    {item ? item.soldToParty : <Skeleton />}
                  </TableCell>
                  <TableCell
                    style={{
                      whiteSpace: "pre-line"
                    }}
                    className={classes.col150}
                  >
                    {item ? `${item.shipTo}` : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.country : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.incoterm : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.payment : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.bu : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.company : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? (
                      item.orderDate ? (
                        item.orderDate
                      ) : null
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col180}>
                    {item ? item.orderStatusSap : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col180}>
                    {item ? (
                      renderStatus(item.orderStatusEOrdering)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>

                  <TableCell
                    className={classNames(
                      classes.colFreeze,
                      isButtonDisabled(item) ? classes.disabledBtn : " "
                    )}
                  >
                    <SVG
                      data-testid="editOrderButton"
                      src={pencilIcon}
                      onClick={() => {
                        if (isButtonDisabled(item)) {
                          return false;
                        }
                        editOrder(
                          item.soNo,
                          item.contractNo,
                          item.salesOrgCode,
                          item.soldToCode
                        );
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data?.length ? (
          <StickyHorizontalScrollBar container={containerRef} />
        ) : null}
      </div>
      <Box className={classes.paginateFooter} data-testid="pagination-wrapper">
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          disabled={disabled}
          isBackFistPage
          isNextLastPage
          isShowIndex
        />
      </Box>
      <DialogCreateOrderError
        onConfirm={toggleCloseCreateOrderError}
        open={openCreateOrderError}
        okText="ปิดหน้าต่าง"
        title="ไม่สามารถดำเนินการต่อได้"
        content={
          <span>
            <MyTypography variant="body2" className={classes.inlineBlock}>
              เนื่องจาก Customer Credit :
            </MyTypography>{" "}
            <MyTypography variant="subtitle2" className={classes.inlineBlock}>
              Credit Blocked
            </MyTypography>
          </span>
        }
      />
    </>
  );
};

export default OrderListTable;
